import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, FileText, CheckCircle2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import SEO from "../components/SEO";
import {
    Collapsible,
    CONTRACTOR_HOLD_HARMLESS_TITLE,
    ContractorHoldHarmless,
    ContractorInsuranceRequirement,
} from "../components/legal/HoldHarmless";

// ============================================================
// PUBLIC pages added on top of the Books module: Financial
// Transparency report + Contractor public application form +
// Micro-grant public application form.
// ============================================================

// -------------------- /financials --------------------
export const Financials = () => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [records, setRecords] = useState([]);
    useEffect(() => {
        api.get("/public/financials", { params: { year } }).then((r) => setData(r.data));
    }, [year]);
    useEffect(() => {
        api.get("/public-records").then((r) => setRecords(r.data || [])).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20" data-testid="public-financials">
            <SEO
                title="Financial transparency · Live P&L and balance sheet"
                description="Live, public-facing financials for Reddog Community Foundation — Statement of Activities, Statement of Financial Position, and Functional Expense Allocation, generated directly from the books."
                path="/financials"
            />
            <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5" strokeWidth={2} />
                    Financial transparency · auto-generated from the public ledger
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-4">
                    Every dollar tracked.
                    <br />
                    <span className="text-[#E53935]">Backed by the public ledger.</span>
                </h1>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-10">
                    <p className="text-sm text-[#4A5568] max-w-3xl">
                        These statements are generated <strong>live</strong> from the foundation's double-entry books at the timestamp shown below.
                        Public-facing figures are summarized from the foundation's books and, where applicable, tied to entries on the{" "}
                        <Link to="/ledger" className="text-[#E53935] font-medium underline-offset-2 hover:underline">Public Ledger</Link>.
                    </p>
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Fiscal year</label>
                        <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} min={2024} max={2100} className="w-24 rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid="financials-year" />
                    </div>
                </div>

                {!data ? <p className="text-sm text-[#4A5568]">Loading…</p> : (
                    <div className="space-y-8">
                        <p className="text-xs text-[#4A5568]">As of {new Date(data.as_of).toLocaleString()}</p>

                        {/* Statement of Activities */}
                        <Section title="Statement of Activities" subtitle={`Income, expense, and net change in net assets · FY ${data.fiscal_year}`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                                    <div className="px-5 py-3 bg-green-50 border-b border-green-100"><p className="text-xs uppercase tracking-wider font-bold text-green-800">Income</p></div>
                                    {data.statement_of_activities.income.length === 0 ? <p className="px-5 py-6 text-sm text-[#4A5568]">No income recorded.</p> : (
                                        <table className="w-full text-sm">
                                            <tbody className="divide-y divide-black/5">
                                                {data.statement_of_activities.income.map((r) => (
                                                    <tr key={r.code}>
                                                        <td className="px-4 py-2 text-[#1C252E]">{r.name}</td>
                                                        <td className="px-4 py-2 text-right font-mono">${r.balance.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                                <tr className="bg-[#F4F1EA]"><td className="px-4 py-3 font-medium">Total income</td><td className="px-4 py-3 text-right font-mono font-bold" data-testid="public-total-income">${data.statement_of_activities.total_income.toFixed(2)}</td></tr>
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                                    <div className="px-5 py-3 bg-red-50 border-b border-red-100"><p className="text-xs uppercase tracking-wider font-bold text-red-800">Expense</p></div>
                                    {data.statement_of_activities.expense.length === 0 ? <p className="px-5 py-6 text-sm text-[#4A5568]">No expense recorded.</p> : (
                                        <table className="w-full text-sm">
                                            <tbody className="divide-y divide-black/5">
                                                {data.statement_of_activities.expense.map((r) => (
                                                    <tr key={r.code}>
                                                        <td className="px-4 py-2 text-[#1C252E]">{r.name}{r.functional && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">{r.functional}</span>}</td>
                                                        <td className="px-4 py-2 text-right font-mono">${r.balance.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                                <tr className="bg-[#F4F1EA]"><td className="px-4 py-3 font-medium">Total expense</td><td className="px-4 py-3 text-right font-mono font-bold" data-testid="public-total-expense">${data.statement_of_activities.total_expense.toFixed(2)}</td></tr>
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 bg-[#F2D8D5]/40 border border-[#E53935]/20 rounded-2xl p-5 flex justify-between items-center" data-testid="public-net-change">
                                <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Net change in net assets</p>
                                <p className={`font-heading text-3xl font-medium ${data.statement_of_activities.net_change_in_assets >= 0 ? "text-green-700" : "text-red-700"}`}>${data.statement_of_activities.net_change_in_assets.toFixed(2)}</p>
                            </div>
                        </Section>

                        {/* Functional Expense */}
                        {data.functional_expense.grand_total > 0 && (
                            <Section title="Functional expense breakdown" subtitle="How expenses split across Program · Management · Fundraising (Form 990 layout)">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {["Program", "Management", "Fundraising", "Uncategorized"].map((b) => (
                                        <div key={b} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`public-fx-${b.toLowerCase()}`}>
                                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mb-1">{b}</p>
                                            <p className="font-heading text-2xl text-[#1C252E] font-medium">${(data.functional_expense.totals[b] || 0).toFixed(2)}</p>
                                            <p className="text-[11px] text-[#4A5568] mt-1">{(data.functional_expense.percentages[b] || 0).toFixed(1)}% of total</p>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {/* Balance Sheet */}
                        <Section title="Statement of Financial Position" subtitle={`Assets, liabilities, and net assets as of end of FY ${data.fiscal_year}`}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <BsBox color="bg-blue-50 text-blue-800 border-blue-100" title="Assets" items={data.financial_position.assets} testid="public-bs-assets" />
                                <BsBox color="bg-amber-50 text-amber-800 border-amber-100" title="Liabilities" items={data.financial_position.liabilities} testid="public-bs-liab" />
                                <BsBox color="bg-purple-50 text-purple-800 border-purple-100" title="Net assets / Equity" items={data.financial_position.equity} extraRow={["Current period net", data.financial_position.current_period_net]} testid="public-bs-equity" />
                            </div>
                            <div className={`mt-4 rounded-2xl p-4 text-xs ${data.financial_position.balanced ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid="public-bs-balanced">
                                Total assets ${data.financial_position.total_assets.toFixed(2)} = Total liabilities ${data.financial_position.total_liabilities.toFixed(2)} + Total net assets ${data.financial_position.total_net_assets.toFixed(2)} {data.financial_position.balanced ? "· Balanced ✓" : "· Out of balance"}
                            </div>
                        </Section>

                        {/* Foundation public records — IRS letters, articles, bylaws */}
                        {records.length > 0 && (
                            <Section title="Foundation records" subtitle="Foundational legal documents — downloadable for verification or grant applications.">
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="public-records-grid">
                                    {records.map((r) => (
                                        <li key={r.id}>
                                            <a
                                                href={r.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`public-record-link-${r.id}`}
                                                className="group flex items-start gap-3 bg-white border border-black/10 rounded-2xl p-4 hover:border-[#E53935]/40 hover:shadow-sm transition"
                                            >
                                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F2D8D5] text-[#E53935] shrink-0">
                                                    <FileText className="w-5 h-5" strokeWidth={1.5} />
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium text-[#1C252E] group-hover:text-[#E53935] transition-colors">{r.title}</p>
                                                    {r.description && <p className="text-xs text-[#4A5568] mt-0.5">{r.description}</p>}
                                                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mt-1.5">Download PDF →</p>
                                                </div>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-xs text-amber-900 leading-relaxed" data-testid="public-disclosure">
                            {data.disclosure}
                        </div>

                        {/* Get involved CTAs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4" data-testid="financials-get-involved">
                            <Link to="/apply/contractor" data-testid="financials-apply-contractor-cta" className="group bg-white border border-black/10 rounded-2xl p-6 hover:border-[#E53935] transition-colors flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935] mb-1">For trades &amp; service providers</p>
                                    <h3 className="font-heading text-xl text-[#1C252E] font-medium group-hover:text-[#E53935] transition-colors">Apply to be a vetted contractor</h3>
                                    <p className="text-xs text-[#4A5568] mt-1">License, insurance, and references reviewed under our conflict-of-interest policy.</p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-[#E53935] mt-1 flex-shrink-0 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                            </Link>
                            <Link to="/apply/grant" data-testid="financials-apply-grant-cta" className="group bg-white border border-black/10 rounded-2xl p-6 hover:border-[#E53935] transition-colors flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935] mb-1">For teachers &amp; community groups</p>
                                    <h3 className="font-heading text-xl text-[#1C252E] font-medium group-hover:text-[#E53935] transition-colors">Apply for a micro-grant</h3>
                                    <p className="text-xs text-[#4A5568] mt-1">Funding for a specific classroom, family, or community project — tracked publicly on the ledger.</p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-[#E53935] mt-1 flex-shrink-0 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Section = ({ title, subtitle, children }) => (
    <div>
        <div className="mb-3">
            <h2 className="font-heading text-2xl text-[#1C252E] font-medium">{title}</h2>
            {subtitle && <p className="text-xs text-[#4A5568] mt-1">{subtitle}</p>}
        </div>
        {children}
    </div>
);

const BsBox = ({ color, title, items, extraRow, testid }) => {
    // On the public Statement of Financial Position we suppress asset lines
    // that would display a negative balance — those are accounting artifacts
    // (e.g. inventory consumed faster than restocked in the demo books) that
    // confuse non-accountant readers. The COGS expense is already counted in
    // the Statement of Activities, so suppressing them here does not affect
    // the bottom-line totals shown above the box.
    const visible = title === "Assets"
        ? (items || []).filter((r) => (r.balance ?? 0) >= 0)
        : (items || []);
    const hiddenCount = (items || []).length - visible.length;
    return (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <div className={`px-5 py-3 border-b ${color}`}><p className="text-xs uppercase tracking-wider font-bold">{title}</p></div>
            {visible.length === 0 && !extraRow ? <p className="px-5 py-6 text-sm text-[#4A5568]">—</p> : (
                <table className="w-full text-sm" data-testid={testid}>
                    <tbody className="divide-y divide-black/5">
                        {visible.map((r) => (
                            <tr key={r.code}><td className="px-4 py-2 text-[#1C252E]">{r.name}</td><td className="px-4 py-2 text-right font-mono">${r.balance.toFixed(2)}</td></tr>
                        ))}
                        {extraRow && <tr className="bg-[#FDFBF7]"><td className="px-4 py-2 text-xs italic text-[#4A5568]">{extraRow[0]}</td><td className="px-4 py-2 text-right font-mono text-xs">${extraRow[1].toFixed(2)}</td></tr>}
                    </tbody>
                </table>
            )}
            {hiddenCount > 0 && (
                <p className="px-4 py-2 text-[10px] italic text-[#4A5568] border-t border-black/5">
                    {hiddenCount} contra-balance line{hiddenCount === 1 ? "" : "s"} reclassified to expenses on the Statement of Activities.
                </p>
            )}
        </div>
    );
};

// -------------------- /apply/contractor --------------------
const emptyContractor = {
    business_name: "", contact_name: "", contact_email: "", contact_phone: "",
    trade: "", license_number: "", insurance_carrier: "", years_in_business: "",
    references: "", coi_disclosure: "", pricing_notes: "", why_us: "",
    service_area: "", emergency_availability: "none",
};
const EMERGENCY_LABELS = {
    none: "I don't offer emergency / off-hours work",
    business_hours: "Business hours only (Mon–Fri)",
    weekends_only: "Weekends only",
    "24_7": "Available 24/7 for emergencies",
};
export const ApplyContractor = () => {
    const [form, setForm] = useState(emptyContractor);
    const [accepted, setAccepted] = useState(false);
    const [w9Ack, setW9Ack] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        if (!accepted) {
            toast.error("Please acknowledge the contractor hold-harmless to submit.");
            return;
        }
        if (!w9Ack) {
            toast.error("Please acknowledge the W-9 requirement to submit.");
            return;
        }
        try {
            const body = { ...form, hold_harmless_accepted: true, w9_acknowledgment: true };
            body.years_in_business = body.years_in_business ? parseInt(body.years_in_business, 10) : null;
            await api.post("/contractors/apply", body);
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Submission failed");
        }
    };

    if (submitted) return (
        <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20" data-testid="apply-contractor-success">
            <div className="max-w-2xl mx-auto px-6 sm:px-8 text-center">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" strokeWidth={1.5} />
                <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium mb-4">Application received</h1>
                <p className="text-[#4A5568] mb-8 leading-relaxed">Thanks for your interest in working with the Reddog Community Foundation. Our board will review your application and respond by email. We follow a strict conflict-of-interest policy — no contractor is guaranteed paid work.</p>
                <Link to="/" className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-7 py-3 text-sm font-medium">
                    Back to home <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20" data-testid="apply-contractor-page">
            <SEO
                title="Apply to be a vetted contractor"
                description="Trades and service providers — apply to join Reddog Community Foundation's vetted contractor registry. License, insurance, references, and conflict-of-interest disclosure reviewed by the board."
                path="/apply/contractor"
            />
            <div className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Vetted contractor application</p>
                <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-3">Apply to be a vetted contractor</h1>
                <p className="text-[#4A5568] leading-relaxed mb-8">Submit your business details below. The board reviews every application against our conflict-of-interest policy. Approved contractors join the foundation's private registry and become eligible for spend-request bids. <strong>No contractor is guaranteed paid work.</strong></p>

                <form onSubmit={save} className="space-y-6 bg-white border border-black/5 rounded-2xl p-6 sm:p-8" data-testid="apply-contractor-form">
                    {/* — 1. Business — */}
                    <ACSection title="Business">
                        <Lbl t="Business name *"><input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-business" /></Lbl>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Lbl t="Contact name *"><input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-contact-name" /></Lbl>
                            <Lbl t="Trade *"><input required value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} placeholder="e.g. Electrician, Carpenter" className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-trade" /></Lbl>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Lbl t="Email *"><input required type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-email" /></Lbl>
                            <Lbl t="Phone"><input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-phone" /></Lbl>
                        </div>
                        <Lbl t="Service area" hint="Where do you work? Helps the board match jobs to nearby contractors first.">
                            <input value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} placeholder="e.g. Frenchtown + within 15 miles" className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-service-area" />
                        </Lbl>
                        <Lbl t="Emergency / off-hours availability">
                            <select value={form.emergency_availability} onChange={(e) => setForm({ ...form, emergency_availability: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" data-testid="ac-emergency">
                                {Object.entries(EMERGENCY_LABELS).map(([slug, label]) => (
                                    <option key={slug} value={slug}>{label}</option>
                                ))}
                            </select>
                        </Lbl>
                    </ACSection>

                    {/* — 2. Credentials — */}
                    <ACSection title="Credentials">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Lbl t="License #"><input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-license" /></Lbl>
                            <Lbl t="Insurance carrier"><input value={form.insurance_carrier} onChange={(e) => setForm({ ...form, insurance_carrier: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-insurance" /></Lbl>
                            <Lbl t="Years in business"><input type="number" min="0" value={form.years_in_business} onChange={(e) => setForm({ ...form, years_in_business: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ac-years" /></Lbl>
                        </div>
                        <Lbl t="References" hint="Names, phone numbers, prior jobs — anyone the board can call."><textarea value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })} rows={3} className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" data-testid="ac-references" /></Lbl>
                        <Lbl t="Conflict-of-interest disclosure *" hint="Required. Disclose any relationship with board members, family ties, or other potential conflicts. Write 'None' if none apply.">
                            <textarea required value={form.coi_disclosure} onChange={(e) => setForm({ ...form, coi_disclosure: e.target.value })} rows={3} className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" data-testid="ac-coi" />
                        </Lbl>
                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="ac-insurance-note">
                            <ContractorInsuranceRequirement />
                        </div>
                    </ACSection>

                    {/* — 3. Your pitch — */}
                    <ACSection title="Your pitch">
                        <Lbl t="Pricing notes" hint="Hourly, fixed-bid, materials markup — whatever helps the board scope the work."><textarea value={form.pricing_notes} onChange={(e) => setForm({ ...form, pricing_notes: e.target.value })} rows={2} className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" data-testid="ac-pricing" /></Lbl>
                        <Lbl t="Why work with Reddog?" hint="What draws you to community work? Optional but appreciated."><textarea value={form.why_us} onChange={(e) => setForm({ ...form, why_us: e.target.value })} rows={2} className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" data-testid="ac-why" /></Lbl>
                    </ACSection>

                    {/* — 4. Acknowledgments — */}
                    <ACSection title="Acknowledgments">
                        {/* W-9 acknowledgment — IRS 1099-NEC compliance */}
                        <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl bg-[#F4F1EA] border border-black/5 px-4 py-3" data-testid="ac-w9-label">
                            <input
                                type="checkbox"
                                checked={w9Ack}
                                onChange={(e) => setW9Ack(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-[#E53935] flex-shrink-0"
                                data-testid="ac-w9-checkbox"
                            />
                            <span className="text-sm text-[#1C252E] leading-relaxed">
                                <strong className="font-medium">I understand a signed IRS Form W-9 is required before any payment.</strong>
                                <span className="block text-xs text-[#4A5568] mt-1">
                                    The foundation cannot release funds to a contractor without a W-9 on file. After approval, you'll receive a request for a signed W-9 — payments are issued only once it's received.
                                </span>
                            </span>
                        </label>

                        <Collapsible title={CONTRACTOR_HOLD_HARMLESS_TITLE} testid="ac-hh-collapsible">
                            <ContractorHoldHarmless />
                        </Collapsible>

                        <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl bg-[#F4F1EA] border border-black/5 px-4 py-3" data-testid="ac-hh-label">
                            <input
                                type="checkbox"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                required
                                data-testid="ac-hh-checkbox"
                                className="mt-1 w-4 h-4 rounded border-black/30 text-[#E53935] focus:ring-[#E53935] flex-shrink-0"
                            />
                            <span className="text-xs text-[#1C252E] leading-relaxed">
                                I have read and agree to the <strong>Contractor / Vendor Hold Harmless, Insurance, and Responsibility Clause</strong>. I understand I am an independent contractor — not an employee, agent, or representative of the foundation — and I am responsible for my own work, employees, subcontractors, tools, vehicles, licenses, permits, insurance, workmanship, safety, and compliance with all applicable laws.
                            </span>
                        </label>
                    </ACSection>

                    <button type="submit" disabled={!accepted} data-testid="ac-submit" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3.5 font-medium disabled:opacity-60 disabled:cursor-not-allowed">Submit application</button>
                </form>
            </div>
        </div>
    );
};

// -------------------- /apply/grant --------------------
const emptyGrant = {
    recipient_name: "", recipient_email: "", organization: "",
    amount_requested: "", purpose: "",
    need_category: "", urgency: "normal", requested_by_role: "",
    publicly_displayable: false,
};
export const ApplyGrant = () => {
    const [form, setForm] = useState(emptyGrant);
    const [submitted, setSubmitted] = useState(false);
    const [options, setOptions] = useState(null);

    useEffect(() => {
        api.get("/grants/form-options").then(({ data }) => setOptions(data)).catch(() => setOptions(null));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                amount_requested: parseFloat(form.amount_requested),
                need_category: form.need_category || null,
                requested_by_role: form.requested_by_role || null,
            };
            await api.post("/grants/apply", payload);
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Submission failed");
        }
    };

    if (submitted) return (
        <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20" data-testid="apply-grant-success">
            <div className="max-w-2xl mx-auto px-6 sm:px-8 text-center">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" strokeWidth={1.5} />
                <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium mb-4">Grant request received</h1>
                <p className="text-[#4A5568] mb-8 leading-relaxed">Thank you for sharing what you'd like to fund. The Reddog board reviews micro-grant requests at regular meetings and will respond by email. {form.urgency === "urgent" && "Your request is flagged urgent — the board will be notified today."}</p>
                <Link to="/" className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-7 py-3 text-sm font-medium">Back to home <ArrowRight className="w-4 h-4" /></Link>
            </div>
        </div>
    );

    const needCategories = options?.need_categories || [];
    const requestedByRoles = options?.requested_by_roles || [];
    const urgencyLevels = options?.urgency_levels || [];

    return (
        <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20" data-testid="apply-grant-page">
            <SEO
                title="Apply for a micro-grant"
                description="Teachers, classrooms, and community groups in Frenchtown — apply for a micro-grant from Reddog Community Foundation. Approved grants are tracked publicly on the ledger."
                path="/apply/grant"
            />
            <div className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Teacher / classroom micro-grant</p>
                <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-3">Apply for a micro-grant</h1>
                <p className="text-[#4A5568] leading-relaxed mb-8">Are you a teacher or classroom in the Frenchtown community with a specific need? Tell us what you'd like to fund and how much you need — the foundation board reviews requests at every meeting.</p>

                <form onSubmit={save} className="space-y-6 bg-white border border-black/5 rounded-2xl p-6 sm:p-8" data-testid="apply-grant-form">
                    {/* — 1. Who you are — */}
                    <ACSection title="Who you are">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Lbl t="Your name *"><input required value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ag-name" /></Lbl>
                            <Lbl t="Email *"><input required type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ag-email" /></Lbl>
                        </div>
                        <Lbl t="You are a *" hint="Helps the board flag conflict-of-interest checks correctly.">
                            <select required value={form.requested_by_role} onChange={(e) => setForm({ ...form, requested_by_role: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" data-testid="ag-requested-by">
                                <option value="">— Select —</option>
                                {requestedByRoles.map((r) => (
                                    <option key={r.slug} value={r.slug}>{r.label}</option>
                                ))}
                            </select>
                        </Lbl>
                        <Lbl t="School / classroom"><input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="e.g. Frenchtown Elementary, 3rd grade Mrs. Martinez" className="w-full rounded-xl border border-black/10 px-4 py-2.5" data-testid="ag-org" /></Lbl>
                    </ACSection>

                    {/* — 2. What you need — */}
                    <ACSection title="What you need">
                        <Lbl t="Need category *" hint="Helps the board route to the right funding bucket.">
                            <select required value={form.need_category} onChange={(e) => setForm({ ...form, need_category: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" data-testid="ag-need-category">
                                <option value="">— Select —</option>
                                {needCategories.map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.label}</option>
                                ))}
                            </select>
                            {form.need_category && (
                                <p className="text-[11px] text-[#4A5568] mt-1.5">
                                    {needCategories.find((c) => c.slug === form.need_category)?.description}
                                </p>
                            )}
                        </Lbl>
                        <Lbl t="Urgency *">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="ag-urgency-group">
                                {urgencyLevels.map((u) => {
                                    const active = form.urgency === u.slug;
                                    const tone = u.slug === "urgent"
                                        ? (active ? "bg-amber-100 border-amber-400 text-amber-900" : "border-amber-200 hover:border-amber-400 text-amber-800")
                                        : (active ? "bg-[#E53935] border-[#E53935] text-white" : "border-black/10 hover:border-[#E53935]");
                                    return (
                                        <button
                                            key={u.slug}
                                            type="button"
                                            onClick={() => setForm({ ...form, urgency: u.slug })}
                                            data-testid={`ag-urgency-${u.slug}`}
                                            className={`text-left rounded-xl border px-3 py-2.5 text-xs transition-colors ${tone}`}
                                        >
                                            <span className="font-bold uppercase tracking-wider">{u.slug}</span>
                                            <span className="block mt-0.5 leading-snug">{u.label.split("—")[1]?.trim() || u.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Lbl>
                        <Lbl t="Amount requested *"><input required type="number" step="0.01" min="0" value={form.amount_requested} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} placeholder="e.g. 250" className="w-full rounded-xl border border-black/10 px-4 py-2.5 font-mono" data-testid="ag-amount" /></Lbl>
                        <Lbl t="Purpose *" hint="What will the money be used for? Be specific."><textarea required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} rows={4} className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" data-testid="ag-purpose" /></Lbl>
                    </ACSection>

                    {/* — 3. Permissions — */}
                    <ACSection title="Permissions">
                        <div className="rounded-xl bg-[#F4F1EA] border border-black/5 p-4" data-testid="ag-public-display-block">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.publicly_displayable}
                                    onChange={(e) => setForm({ ...form, publicly_displayable: e.target.checked })}
                                    className="mt-1 w-4 h-4 accent-[#E53935] flex-shrink-0"
                                    data-testid="ag-public-display"
                                />
                                <span className="text-sm text-[#1C252E] leading-relaxed">
                                    <strong className="font-medium">It is OK to publicly display this request if approved.</strong>
                                    <span className="block text-xs text-[#4A5568] mt-1">
                                        If checked, the recipient name, school/classroom, purpose, and amount may appear on the public Impact Wall and ledger detail page once the grant is paid. <strong>Leave unchecked</strong> if this involves a student or family that should stay anonymous — the foundation will still record and pay the grant, but with no public attribution.
                                    </span>
                                </span>
                            </label>
                        </div>
                    </ACSection>

                    <button type="submit" data-testid="ag-submit" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3.5 font-medium">Submit request</button>
                </form>
            </div>
        </div>
    );
};

const Lbl = ({ t, hint, children }) => (
    <div>
        <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">{t}</label>
        {children}
        {hint && <p className="text-[11px] text-[#4A5568] mt-1.5">{hint}</p>}
    </div>
);

// Visual section wrapper for grouped public-form fields (matches the
// admin-modal section pattern, but in light-card / public context).
const ACSection = ({ title, children }) => (
    <section className="space-y-3 pb-5 border-b border-black/5 last:border-0 last:pb-0">
        <h2 className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935]">{title}</h2>
        {children}
    </section>
);
